import {
  useRecognitionCategories,
  useRecognitionCategoriesSort,
} from "@/queries/use-recognitions.query.ts";
import { ApiRecognitionCategory } from "@/types/recognitions/recognition.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Button,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function RecognitionCategorySortModal(props: ModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  const { data: categories, isPending } = useRecognitionCategories();
  const categoriesSort = useRecognitionCategoriesSort();

  const syncKey = (categories ?? []).map((item) => item.id).join(",");

  const [sorted, setSorted] = useState<ApiRecognitionCategory[]>(categories ?? []);
  const [syncedKey, setSyncedKey] = useState(syncKey);

  if (syncKey !== syncedKey) {
    setSyncedKey(syncKey);
    setSorted(categories ?? []);
  }

  const saveOrder = () => {
    categoriesSort.mutate(
      sorted.map((category) => category.id),
      { onSuccess: () => close(id) }
    );
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const items = Array.from(sorted);
    const [moved] = items.splice(source.index, 1);
    items.splice(destination.index, 0, moved);
    setSorted(items);
  };

  return (
    <Modal
      title={t("common:reordering")}
      {...props}
      withCloser={true}
      actions={
        <Stack>
          <Button size={"lg"} onClick={saveOrder} isLoading={categoriesSort.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      {isPending ? (
        <Stack direction={"vertical"} gap={"md"}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className={"h-4 w-48"} />
          ))}
        </Stack>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={"recognition-categories-list"} direction={"vertical"}>
            {(provided) => (
              <div
                className={"w-full flex flex-col overflow-y-auto"}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sorted.map((category, index) => (
                  <Draggable key={category.id} draggableId={category.id.toString()} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={"flex items-center gap-4 bg-light py-2 min-h-16"}
                      >
                        <div
                          className={"flex items-center justify-center cursor-grab text-secondary"}
                        >
                          <FontAwesomeIcon icon={"fa-light fa-grip-dots-vertical"} />
                        </div>
                        <Title header={category.title} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Modal>
  );
}
